// This is the SUPPORTED_CURRENCIES list from wf.utils.currencies, but in alphabetical order

import type { Item } from "@wayflyer/flyui";
import type { IntlShape } from "react-intl";

import type { SupportedCountry } from "./countries";

// c.f. https://github.com/wayflyer/utils/blob/main/wf/utils/currencies.py
export type Currency =
  | "AUD"
  | "CAD"
  | "CHF"
  | "DKK"
  | "EUR"
  | "GBP"
  | "HKD"
  | "HUF"
  | "JPY"
  | "MXN"
  | "NOK"
  | "NZD"
  | "PLN"
  | "RUB"
  | "SEK"
  | "SGD"
  | "USD";

export const CURRENCIES = [
  "AUD",
  "CAD",
  "CHF",
  "DKK",
  "EUR",
  "GBP",
  "HKD",
  "HUF",
  "JPY",
  "MXN",
  "NOK",
  "NZD",
  "PLN",
  "RUB",
  "SEK",
  "SGD",
  "USD",
] as const satisfies readonly Currency[];

export type SupportedCurrency = Extract<Currency, "EUR" | "GBP" | "USD" | "AUD" | "CAD" | "DKK" | "SEK">;

export const SUPPORTED_CURRENCIES = [
  "EUR",
  "GBP",
  "USD",
  "AUD",
  "CAD",
  "DKK",
  "SEK",
] as const satisfies readonly SupportedCurrency[];

export const isSupportedCurrency = (currency: string): currency is SupportedCurrency =>
  (SUPPORTED_CURRENCIES as readonly string[]).includes(currency);

export const MCA_CURRENCY_OPTIONS: { label: SupportedCurrency; value: SupportedCurrency }[] = [
  { label: "EUR", value: "EUR" },
  { label: "USD", value: "USD" },
  { label: "GBP", value: "GBP" },
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "DKK", value: "DKK" },
  { label: "SEK", value: "SEK" },
];

export const currencyOptions: Item<Currency>[] = MCA_CURRENCY_OPTIONS.map((currency) => {
  return { id: currency.value, text: currency.label };
});

export const isEuroCountry = (countryCode: string) => {
  return ["BE", "DE", "IE", "ES", "NL"].includes(countryCode);
};

export const COUNTRY_CURRENCY_MAP: Record<string, SupportedCurrency> = {
  AU: "AUD",
  CA: "CAD",
  DK: "DKK",
  EU: "EUR",
  GB: "GBP",
  SE: "SEK",
  US: "USD",
};

export const getCurrencyFromCountry = (countryCode: SupportedCountry): SupportedCurrency => {
  const country = isEuroCountry(countryCode) ? "EU" : countryCode;
  return COUNTRY_CURRENCY_MAP[country];
};

// Normalized (exchange?) rate to USD, based on Routing by Revenue Notion page
// https://www.notion.so/wayflyer/Credit-Connection-Document-Requirements-2c00c0591cdf800e8b5bc3398944efcc
export const NormalizedRateToUSD: Record<string, number> = {
  USD: 1,
  CAD: 2,
  GBP: 1,
  AUD: 2,
  EUR: 1,
  DKK: 7,
  SEK: 10,
} as const;

export function normalizeToUSD(amount: number, currencyCode: string) {
  if (!amount || !currencyCode || !NormalizedRateToUSD[currencyCode]) {
    return 0;
  }
  return amount / NormalizedRateToUSD[currencyCode];
}

export const getMinEligibleRevenue = (revenueCurrency: SupportedCurrency) => {
  return NormalizedRateToUSD[revenueCurrency] * 10_000;
};

export const getFormattedMinEligibleRevenue = (intl: IntlShape, revenueCurrency: SupportedCurrency) => {
  const minRevenue = getMinEligibleRevenue(revenueCurrency);

  return intl.formatNumber(minRevenue, {
    style: "currency",
    currency: revenueCurrency,
    currencyDisplay: revenueCurrency === "USD" ? "narrowSymbol" : "symbol",
    maximumFractionDigits: 0,
  });
};
